export interface ContactData {
  name: string | null;
  title: string | null;
  phone: string | null;
  email: string | null;
}

// "windows": vCard 2.1 mit Windows-1252-Quoted-Printable für Outlook Classic.
// "standard": vCard 3.0 als UTF-8 für alle übrigen Plattformen (Android,
// iOS/macOS, Google Contacts, iCloud) - der dokumentierte Konsens dort.
export type VCardVariant = "windows" | "standard";

export const VCARD_MIME_TYPE = "text/vcard";

const MAX_QUOTED_PRINTABLE_CONTENT_LENGTH = 75;

// Unicode-Zeichen, die in Windows-1252 auf die Bytes 0x80-0x9F abgebildet
// werden (alle übrigen Codepoints bis 0xFF entsprechen direkt dem Byte-Wert).
const WINDOWS_1252_EXTRA_BYTES: Record<string, number> = {
  "€": 0x80,
  "‚": 0x82,
  ƒ: 0x83,
  "„": 0x84,
  "…": 0x85,
  "†": 0x86,
  "‡": 0x87,
  ˆ: 0x88,
  "‰": 0x89,
  Š: 0x8a,
  "‹": 0x8b,
  Œ: 0x8c,
  Ž: 0x8e,
  "‘": 0x91,
  "’": 0x92,
  "“": 0x93,
  "”": 0x94,
  "•": 0x95,
  "–": 0x96,
  "—": 0x97,
  "˜": 0x98,
  "™": 0x99,
  š: 0x9a,
  "›": 0x9b,
  œ: 0x9c,
  ž: 0x9e,
  Ÿ: 0x9f,
};

const encodeWindows1252 = (value: string) => {
  const bytes: number[] = [];

  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;

    if (codePoint <= 0x7f || (codePoint >= 0xa0 && codePoint <= 0xff)) {
      bytes.push(codePoint);
    } else if (character in WINDOWS_1252_EXTRA_BYTES) {
      bytes.push(WINDOWS_1252_EXTRA_BYTES[character]);
    } else {
      return null;
    }
  }

  return Uint8Array.from(bytes);
};

const encodeQuotedPrintableTokens = (bytes: Uint8Array) =>
  Array.from(bytes, (byte) => {
    const isPrintableAscii =
      (byte >= 33 && byte <= 60) || (byte >= 62 && byte <= 126);

    return isPrintableAscii
      ? String.fromCharCode(byte)
      : `=${byte.toString(16).toUpperCase().padStart(2, "0")}`;
  });

// Outlook Classic (Windows) ignoriert den CHARSET-Parameter und interpretiert
// Quoted-Printable-Bytes in der System-ANSI-Codepage (Windows-1252). Deshalb
// werden die Werte als Windows-1252-Bytes kodiert: Outlook Classic liest sie
// korrekt, und Parser, die den Parameter respektieren, ebenfalls. Das Label
// steht klein, weil Outlook bei CHARSET-Werten case-sensitiv sein kann.
// Zeichen außerhalb von Windows-1252 erzwingen einen UTF-8-Fallback für das
// betroffene Feld (in Outlook Classic prinzipbedingt nicht darstellbar).
const formatQuotedPrintableProperty = (property: string, value: string) => {
  const windows1252Bytes = encodeWindows1252(value);
  const charset = windows1252Bytes ? "windows-1252" : "utf-8";
  const bytes = windows1252Bytes ?? new TextEncoder().encode(value);

  const lines: string[] = [];
  let currentLine = `${property};CHARSET=${charset};ENCODING=QUOTED-PRINTABLE:`;

  for (const token of encodeQuotedPrintableTokens(bytes)) {
    if (
      currentLine.length + token.length >
      MAX_QUOTED_PRINTABLE_CONTENT_LENGTH
    ) {
      lines.push(`${currentLine}=`);
      currentLine = "";
    }

    currentLine += token;
  }

  lines.push(currentLine);
  return lines.join("\r\n");
};

// Zeilenumbrüche in Werten werden als CRLF normalisiert und landen dadurch
// Quoted-Printable-kodiert (=0D=0A) im Feld - das vCard-2.1-konforme Format.
const normalizeLineBreaks = (value: string) =>
  value
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .replaceAll("\n", "\r\n");

// In vCard 2.1 wird nur in strukturierten Feldern (N) das Semikolon maskiert.
const escapeV21StructuredComponent = (value: string) =>
  value.replaceAll("\\", "\\\\").replaceAll(";", "\\;");

// vCard 3.0 maskiert Backslash, Semikolon, Komma und Zeilenumbrüche
// in allen Textwerten (RFC 2426).
const escapeV3TextValue = (value: string) =>
  value
    .replaceAll("\\", "\\\\")
    .replaceAll("\r\n", "\\n")
    .replaceAll("\r", "\\n")
    .replaceAll("\n", "\\n")
    .replaceAll(";", "\\;")
    .replaceAll(",", "\\,");

const sanitizeDirectValue = (value: string) =>
  value.replaceAll(/[\r\n;\\]+/g, " ").trim();

const splitName = (name: string | null) => {
  const nameParts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const familyName = nameParts.pop() ?? "";
  const givenName = nameParts.join(" ");

  return { familyName, givenName };
};

const generateWindowsVCard = (contactData: ContactData) => {
  const { familyName, givenName } = splitName(contactData.name);
  const structuredName = `${escapeV21StructuredComponent(familyName)};${escapeV21StructuredComponent(givenName)};;;`;
  const name = normalizeLineBreaks(contactData.name ?? "");
  const title = normalizeLineBreaks(contactData.title ?? "");

  const lines = [
    "BEGIN:VCARD",
    "VERSION:2.1",
    formatQuotedPrintableProperty("N", structuredName),
    formatQuotedPrintableProperty("FN", name),
    ...(title ? [formatQuotedPrintableProperty("TITLE", title)] : []),
    ...(contactData.phone
      ? [`TEL;WORK;VOICE:${sanitizeDirectValue(contactData.phone)}`]
      : []),
    ...(contactData.email
      ? [`EMAIL;INTERNET:${sanitizeDirectValue(contactData.email)}`]
      : []),
    "ORG:WIEST GROUP",
    "END:VCARD",
  ];

  return `${lines.join("\r\n")}\r\n`;
};

// UTF-8-Datei ohne BOM und ohne Quoted-Printable. Der (in 3.0 eigentlich
// nicht vorgesehene) Parameter CHARSET=utf-8 bleibt gesetzt, weil er
// charset-ratende Importer korrigiert und nirgends dokumentiert schadet.
const generateStandardVCard = (contactData: ContactData) => {
  const { familyName, givenName } = splitName(contactData.name);
  const structuredName = `${escapeV3TextValue(familyName)};${escapeV3TextValue(givenName)};;;`;

  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N;CHARSET=utf-8:${structuredName}`,
    `FN;CHARSET=utf-8:${escapeV3TextValue(contactData.name ?? "")}`,
    ...(contactData.title
      ? [`TITLE;CHARSET=utf-8:${escapeV3TextValue(contactData.title)}`]
      : []),
    ...(contactData.phone
      ? [`TEL;TYPE=WORK,VOICE:${sanitizeDirectValue(contactData.phone)}`]
      : []),
    ...(contactData.email
      ? [`EMAIL;TYPE=INTERNET:${sanitizeDirectValue(contactData.email)}`]
      : []),
    "ORG:WIEST GROUP",
    "END:VCARD",
  ];

  return `${lines.join("\r\n")}\r\n`;
};

export const generateVCard = (
  contactData: ContactData,
  variant: VCardVariant
) =>
  variant === "windows"
    ? generateWindowsVCard(contactData)
    : generateStandardVCard(contactData);
